import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { overlayStyle } from '../../styles/modalStyles';
import { COLORS, Z_INDEX } from '../../utils/constants';
import { ensureRoomInvites, buildShareLinks, inviteByEmail } from '../../services/roomService';
import { useEmbeddedDialog } from '../../utils/embedDialog';
import { showError } from '../../utils/notices';
import '../../styles/shareModal.css';

function ShareModal({
  roomName,
  isOpen,
  onClose,
  hostName,
  candidateEmail: candidateEmailProp = '',
  onCandidateEmailSaved,
}) {
  const [hostLink, setHostLink] = useState('');
  const [teamLink, setTeamLink] = useState('');
  const [candidateLink, setCandidateLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teamEmail, setTeamEmail] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteRoleBusy, setInviteRoleBusy] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');
  const [copiedKey, setCopiedKey] = useState('');

  useEffect(() => {
    if (!isOpen || !roomName) return undefined;
    let cancelled = false;
    const local = buildShareLinks(roomName);
    setHostLink(local.hostLink);
    setTeamLink(local.teamLink);
    setCandidateLink(local.candidateLink);
    (async () => {
      setLoading(true);
      try {
        await ensureRoomInvites(roomName, { hostName });
        if (!cancelled) {
          const links = buildShareLinks(roomName);
          setHostLink(links.hostLink);
          setTeamLink(links.teamLink);
          setCandidateLink(links.candidateLink);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to prepare share links');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, roomName, hostName]);

  useEffect(() => {
    if (!isOpen) {
      setInviteMessage('');
      setInviteStatus('');
      setError('');
      setTeamEmail('');
      setInviteBusy(false);
      setInviteRoleBusy('');
      setCopiedKey('');
      return;
    }
    if (candidateEmailProp) setCandidateEmail(candidateEmailProp);
  }, [isOpen, candidateEmailProp]);

  const copyLink = async (link, key) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      showError({
        title: "Couldn't copy the link",
        message: 'Select the link and copy it manually.',
        details: [{ label: 'Link', value: link }],
      });
    }
  };

  const sendEmailInvite = async (role) => {
    const email = role === 'candidate' ? candidateEmail : teamEmail;
    setInviteBusy(true);
    setInviteRoleBusy(role);
    setInviteMessage('');
    setInviteStatus('');
    setError('');
    try {
      const result = await inviteByEmail(roomName, { role, email });
      if (role === 'candidate' && onCandidateEmailSaved) {
        onCandidateEmailSaved(result.email || email);
      }
      if (result.emailSent) {
        setInviteStatus('success');
        setInviteMessage(result.message || `Invite email sent to ${result.email}`);
      } else if (result.emailSkipped) {
        setInviteStatus('warning');
        setInviteMessage(
          result.message ||
            'Invite saved. Email is not configured — copy the link instead.'
        );
      } else {
        setInviteStatus('error');
        setError(
          result.message ||
            result.emailError ||
            'Invite saved, but sending email failed.'
        );
        if (result.inviteLink) {
          setInviteMessage(`Share this link: ${result.inviteLink}`);
        }
      }
    } catch (err) {
      setInviteStatus('error');
      setError(err.message || 'Failed to invite by email');
    } finally {
      setInviteBusy(false);
      setInviteRoleBusy('');
    }
  };

  useEmbeddedDialog(isOpen);
  if (!isOpen) return null;

  const statusBanner = (inviteMessage || error) && (
    <div
      role="status"
      style={{
        marginBottom: 14,
        padding: '10px 12px',
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.4,
        background:
          inviteStatus === 'success'
            ? '#ecfdf5'
            : inviteStatus === 'warning'
              ? '#fffbeb'
              : '#fef2f2',
        color:
          inviteStatus === 'success'
            ? '#15803d'
            : inviteStatus === 'warning'
              ? '#b45309'
              : COLORS.error,
        border: `1px solid ${
          inviteStatus === 'success'
            ? '#a7f3d0'
            : inviteStatus === 'warning'
              ? '#fde68a'
              : '#fecaca'
        }`,
      }}
    >
      {error ? <div>{error}</div> : null}
      {inviteMessage ? <div>{inviteMessage}</div> : null}
    </div>
  );

  return (
    <div style={{ ...overlayStyle, zIndex: Z_INDEX.shareModal }} onClick={onClose}>
      <div className="gg-share-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="gg-share-modal__x" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </button>

        <h2 className="gg-share-modal__title">Share invite links</h2>
        <p className="gg-share-modal__subtitle">
          Keep your host link for yourself. Share team or candidate links with others, or invite by email.
        </p>

        {loading ? <p className="gg-share-modal__hint">Preparing links…</p> : null}
        {statusBanner}

        <section className="gg-share-card">
          <div className="gg-share-card__head">
            <VideocamOutlinedIcon className="gg-share-card__icon" fontSize="small" />
            <span className="gg-share-card__heading">Your host link</span>
            <span className="gg-share-card__tag">/host</span>
          </div>
          <p className="gg-share-card__note" style={{ marginTop: 0 }}>
            Keep this for yourself — host controls and Sage. Invite others with the team link below.
          </p>
          <label className="gg-share-card__label">Host link</label>
          <div className="gg-share-card__row">
            <input className="gg-share-card__input" type="text" value={hostLink} readOnly />
            <button
              type="button"
              className="gg-share-card__copy"
              disabled={!hostLink}
              onClick={() => copyLink(hostLink, 'host')}
            >
              <ContentCopyIcon style={{ fontSize: 16 }} />
              {copiedKey === 'host' ? 'Copied' : 'Copy'}
            </button>
          </div>
        </section>

        <section className="gg-share-card">
          <div className="gg-share-card__head">
            <GroupAddOutlinedIcon className="gg-share-card__icon" fontSize="small" />
            <span className="gg-share-card__heading">Invite teammates</span>
            <span className="gg-share-card__tag">/team</span>
          </div>

          <label className="gg-share-card__label">Teammate link</label>
          <div className="gg-share-card__row">
            <input className="gg-share-card__input" type="text" value={teamLink} readOnly />
            <button
              type="button"
              className="gg-share-card__copy"
              disabled={!teamLink}
              onClick={() => copyLink(teamLink, 'team')}
            >
              <ContentCopyIcon style={{ fontSize: 16 }} />
              {copiedKey === 'team' ? 'Copied' : 'Copy'}
            </button>
          </div>

          <label className="gg-share-card__label">Or invite by email</label>
          <div className="gg-share-card__row">
            <input
              className="gg-share-card__input gg-share-card__input--email"
              type="email"
              value={teamEmail}
              onChange={(e) => setTeamEmail(e.target.value)}
              placeholder="name@company.com"
            />
            <button
              type="button"
              className="gg-share-card__send"
              disabled={inviteBusy || !teamEmail.trim()}
              onClick={() => sendEmailInvite('team')}
            >
              {inviteRoleBusy === 'team' ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </section>

        <section className="gg-share-card">
          <div className="gg-share-card__head">
            <PersonOutlineIcon className="gg-share-card__icon" fontSize="small" />
            <span className="gg-share-card__heading">Invite candidates</span>
            <span className="gg-share-card__tag">/candidate</span>
          </div>

          <label className="gg-share-card__label">Candidate link</label>
          <div className="gg-share-card__row">
            <input className="gg-share-card__input" type="text" value={candidateLink} readOnly />
            <button
              type="button"
              className="gg-share-card__copy"
              disabled={!candidateLink}
              onClick={() => copyLink(candidateLink, 'candidate')}
            >
              <ContentCopyIcon style={{ fontSize: 16 }} />
              {copiedKey === 'candidate' ? 'Copied' : 'Copy'}
            </button>
          </div>

          <label className="gg-share-card__label">Or invite by email</label>
          <div className="gg-share-card__row">
            <input
              className="gg-share-card__input gg-share-card__input--email"
              type="email"
              value={candidateEmail}
              onChange={(e) => setCandidateEmail(e.target.value)}
              placeholder="name@email.com"
              disabled={Boolean(candidateEmailProp)}
            />
            <button
              type="button"
              className="gg-share-card__send"
              disabled={inviteBusy || !candidateEmail.trim()}
              onClick={() => sendEmailInvite('candidate')}
            >
              {inviteRoleBusy === 'candidate' ? 'Sending…' : 'Send invite'}
            </button>
          </div>
          {candidateEmailProp ? (
            <p className="gg-share-card__note">Already invited: {candidateEmailProp}</p>
          ) : null}
        </section>

        <button type="button" className="gg-share-modal__close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export default ShareModal;
